// Global singleton cache that persists across React remounts
// This ensures session data survives component unmounting when tab loses focus

import type { Session } from '@supabase/supabase-js'
import type { UserRole } from '@planner/types'

const SESSION_CACHE_KEY = 'supabase.auth.session'
const USER_ROLE_CACHE_KEY = 'supabase.auth.userRole'
const USER_SUPERADMIN_CACHE_KEY = 'supabase.auth.isSuperadmin'

// In-memory cache that survives React remounts
let memoryCache: {
  session: Session | null
  userRole: UserRole | null
  isSuperadmin: boolean
  timestamp: number
} | null = null

export const SessionCache = {
  // Get session from cache (memory first, then localStorage)
  getSession(): Session | null {
    console.log('[SessionCache] getSession called')
    
    // Try memory cache first (fastest)
    if (memoryCache && Date.now() - memoryCache.timestamp < 300000) { // 5 minutes
      console.log('[SessionCache] Returning from memory cache, age:', Date.now() - memoryCache.timestamp, 'ms')
      return memoryCache.session
    }
    
    // Try localStorage
    if (typeof window === 'undefined') {
      console.log('[SessionCache] SSR - no window')
      return null
    }
    
    try {
      const cached = localStorage.getItem(SESSION_CACHE_KEY)
      if (cached) {
        const parsed = JSON.parse(cached)
        if (parsed?.user) {
          console.log('[SessionCache] Returning from localStorage, user:', parsed.user.email)
          // Update memory cache
          const roleCache = localStorage.getItem(USER_ROLE_CACHE_KEY)
          const superadminCache = localStorage.getItem(USER_SUPERADMIN_CACHE_KEY)
          memoryCache = {
            session: parsed,
            userRole: (roleCache as UserRole) || null,
            isSuperadmin: superadminCache === 'true',
            timestamp: Date.now()
          }
          return parsed
        }
      }
      console.log('[SessionCache] No valid session in localStorage')
    } catch (e) {
      console.error('[SessionCache] Error reading cache:', e)
    }
    
    return null
  },

  // Get user role from cache
  getUserRole(): UserRole | null {
    console.log('[SessionCache] getUserRole called')
    
    // Try memory cache first
    if (memoryCache && Date.now() - memoryCache.timestamp < 300000) {
      console.log('[SessionCache] Returning role from memory cache:', memoryCache.userRole)
      return memoryCache.userRole
    }
    
    // Try localStorage
    if (typeof window === 'undefined') return null
    
    try {
      const cached = localStorage.getItem(USER_ROLE_CACHE_KEY)
      console.log('[SessionCache] Role from localStorage:', cached)
      return (cached as UserRole) || null
    } catch (e) {
      console.error('[SessionCache] Error reading role cache:', e)
    }
    
    return null
  },

  // Get superadmin flag from cache
  getIsSuperadmin(): boolean {
    console.log('[SessionCache] getIsSuperadmin called')

    if (memoryCache && Date.now() - memoryCache.timestamp < 300000) {
      console.log('[SessionCache] Returning isSuperadmin from memory cache:', memoryCache.isSuperadmin)
      return memoryCache.isSuperadmin
    }

    if (typeof window === 'undefined') return false

    try {
      const cached = localStorage.getItem(USER_SUPERADMIN_CACHE_KEY)
      return cached === 'true'
    } catch (e) {
      console.error('[SessionCache] Error reading superadmin cache:', e)
    }

    return false
  },

  // Set session in cache (both memory and localStorage)
  setSession(session: Session | null, userRole: UserRole | null, isSuperadmin: boolean = false): void {
    console.log('[SessionCache] setSession called, user:', session?.user?.email, 'role:', userRole)
    
    // Update memory cache
    memoryCache = {
      session,
      userRole,
      isSuperadmin,
      timestamp: Date.now()
    }
    
    // Update localStorage
    if (typeof window === 'undefined') return
    
    try {
      if (session) {
        localStorage.setItem(SESSION_CACHE_KEY, JSON.stringify(session))
        console.log('[SessionCache] Session saved to localStorage')
      } else {
        localStorage.removeItem(SESSION_CACHE_KEY)
        console.log('[SessionCache] Session removed from localStorage')
      }
      
      if (userRole) {
        localStorage.setItem(USER_ROLE_CACHE_KEY, userRole)
        console.log('[SessionCache] Role saved to localStorage:', userRole)
      } else {
        localStorage.removeItem(USER_ROLE_CACHE_KEY)
        console.log('[SessionCache] Role removed from localStorage')
      }

      localStorage.setItem(USER_SUPERADMIN_CACHE_KEY, isSuperadmin ? 'true' : 'false')
    } catch (e) {
      console.error('[SessionCache] Error writing cache:', e)
    }
  },

  // Check if cache exists (fast check without parsing)
  hasCache(): boolean {
    // Check memory cache first
    if (memoryCache && Date.now() - memoryCache.timestamp < 300000) {
      const hasCache = !!memoryCache.session?.user
      console.log('[SessionCache] hasCache (memory):', hasCache)
      return hasCache
    }
    
    // Check localStorage
    if (typeof window === 'undefined') return false
    
    try {
      const cached = localStorage.getItem(SESSION_CACHE_KEY)
      if (cached) {
        const parsed = JSON.parse(cached)
        const hasCache = !!parsed?.user
        console.log('[SessionCache] hasCache (localStorage):', hasCache, 'user:', parsed?.user?.email)
        return hasCache
      }
    } catch (e) {
      console.error('[SessionCache] Error checking cache:', e)
    }
    
    console.log('[SessionCache] hasCache: false (no cache found)')
    return false
  },

  // Clear all cache
  clear(): void {
    console.log('[SessionCache] clear called')
    memoryCache = null
    if (typeof window !== 'undefined') {
      localStorage.removeItem(SESSION_CACHE_KEY)
      localStorage.removeItem(USER_ROLE_CACHE_KEY)
      localStorage.removeItem(USER_SUPERADMIN_CACHE_KEY)
    }
  }
}
